import React, { useEffect, useState } from 'react';
import { Navbar } from './components/Navbar';
import { HeroSection } from './components/HeroSection';
import { ProblemSection } from './components/ProblemSection';
import { MissionSection } from './components/MissionSection';
import { ProcessSection } from './components/ProcessSection';
import { CostBreakdown } from './components/CostBreakdown';
import { ServicesSection } from './components/ServicesSection';
import { FAQSection } from './components/FAQSection';
import { CTASection } from './components/CTASection';
import { Footer } from './components/Footer';
import { CarFinderPage } from './pages/CarFinderPage';
import { MyCarsPage } from './pages/MyCarsPage';
import { AuthPage } from './pages/AuthPage';
import { AdminPage } from './pages/AdminPage';
import { useAuth } from './hooks/useAuth';
type Page = 'home' | 'car-finder' | 'my-cars' | 'auth' | 'admin';
// Map pages to URL paths
const PAGE_PATHS: Record<Page, string> = {
  home: '/',
  'car-finder': '/carfinder',
  'my-cars': '/dashboard',
  auth: '/login',
  admin: '/admindashboard'
};
// Reverse lookup: path → page
function getPageFromPath(path: string): Page | null {
  const clean = path.replace(/\/$/, '') || '/';
  for (const [page, pagePath] of Object.entries(PAGE_PATHS)) {
    if (clean === pagePath) return page as Page;
  }
  return null;
}
export function App() {
  const [currentPage, setCurrentPage] = useState<Page>(() => {
    const fromUrl = getPageFromPath(window.location.pathname);
    return fromUrl || 'home';
  });
  const [returnTo, setReturnTo] = useState<Page | null>(null);
  const {
    isAuthenticated,
    loading,
    user
  } = useAuth();
  const ADMIN_EMAIL = 'earivassb@gmail.com';
  const isAdmin = user?.email === ADMIN_EMAIL;
  // Always set the browser tab title
  useEffect(() => {
    document.title = 'EAR Import Auto';
  }, []);
  // Update URL path when page changes
  useEffect(() => {
    const targetPath = PAGE_PATHS[currentPage] || '/';
    if (window.location.pathname !== targetPath) {
      window.history.pushState(null, '', targetPath);
    }
  }, [currentPage]);
  // Handle browser back/forward buttons
  useEffect(() => {
    const handlePopState = () => {
      const page = getPageFromPath(window.location.pathname);
      if (page) {
        setCurrentPage(page);
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);
  // Auto-redirect after auth
  useEffect(() => {
    if (!loading && isAuthenticated) {
      if (currentPage === 'auth') {
        // If there's a returnTo destination, go there; otherwise go to dashboard
        const destination = returnTo || 'my-cars';
        setReturnTo(null);
        setCurrentPage(destination);
        window.scrollTo(0, 0);
      }
      // Handle Supabase auth tokens from email confirmation
      if (window.location.hash.includes('access_token')) {
        const destination = returnTo || 'my-cars';
        setReturnTo(null);
        setCurrentPage(destination);
        window.scrollTo(0, 0);
        window.history.replaceState(null, '', PAGE_PATHS[destination]);
      }
    }
  }, [isAuthenticated, loading, currentPage, returnTo]);
  const navigateTo = (page: string) => {
    // "Tu carro ideal" and "Mis Solicitudes" both require login
    if ((page === 'car-finder' || page === 'my-cars') && !isAuthenticated) {
      setReturnTo(page as Page);
      setCurrentPage('auth');
      window.scrollTo(0, 0);
      return;
    }
    if (page === 'admin' && !isAdmin) {
      setCurrentPage('home');
      return;
    }
    setCurrentPage(page as Page);
    window.scrollTo(0, 0);
  };
  return <div className="min-h-screen bg-earia-bg text-earia-text-heading font-sans selection:bg-earia-accent selection:text-earia-text-heading" data-id="element-0">
      {currentPage === 'home' ? <>
          <Navbar onNavigate={navigateTo} data-id="element-1" />
          <main data-id="element-2">
            <HeroSection onNavigate={navigateTo} data-id="element-3" />
            <ProblemSection data-id="element-4" />
            <ProcessSection data-id="element-5" />
            <CostBreakdown data-id="element-6" />
            <ServicesSection data-id="element-7" />
            <MissionSection data-id="element-8" />
            <FAQSection data-id="element-9" />
            <CTASection data-id="element-10" />
          </main>
          <Footer data-id="element-11" />
        </> : currentPage === 'car-finder' ? <CarFinderPage onBack={() => navigateTo('home')} onNavigate={navigateTo} data-id="element-12" /> : currentPage === 'auth' ? <AuthPage onBack={() => {
      setReturnTo(null);
      navigateTo('home');
    }} onSuccess={() => {
      const destination = returnTo || 'my-cars';
      setReturnTo(null);
      setCurrentPage(destination);
      window.scrollTo(0, 0);
    }} data-id="element-13" /> : currentPage === 'admin' && isAdmin ? <AdminPage onNavigate={navigateTo} data-id="element-14" /> : <MyCarsPage onBack={() => navigateTo('home')} onNavigate={navigateTo} data-id="element-15" />}
    </div>;
}